import './traction.css'
import { useI18n } from '../lib/i18n'
import { Reveal } from '../components/Reveal'
import { SectionHeader } from '../components/SectionHeader'
import { HudCard } from '../components/HudCard'
import { MetricTicker } from '../components/MetricTicker'

type Stat = { value: number; decimals: number; suffix: string; prefix?: string; label: string }
type Milestone = { n: string; window: string; title: string; body: string }

const DATA = {
  vi: {
    kicker: 'TRACTION',
    title: <>Traction và <em>90 ngày tới</em>.</>,
    sub: 'Các con số dưới đây là mục tiêu minh hoạ cho giai đoạn pilot — không phải kết quả đã đạt. Chúng đặt ra để đo lường tiến độ một cách trung thực.',
    targetTag: 'Mục tiêu',
    stats: [
      { value: 5000, decimals: 0, suffix: '', label: 'thửa bản đồ hoá (mục tiêu pilot)' },
      { value: 1200, decimals: 0, suffix: ' ha', label: 'diện tích phủ kiểm (mục tiêu)' },
      { value: 8, decimals: 0, suffix: '', label: 'HTX tham gia pilot (mục tiêu)' },
      { value: 90, decimals: 0, suffix: '%', label: 'verdict tạo tự động (mục tiêu)' },
    ] as Stat[],
    roadmapTag: '// 07 · 90 NGÀY TỚI',
    roadmapTitle: 'Lộ trình 90 ngày tới',
    roadmapNote: 'Mục tiêu, chưa phải cam kết kết quả.',
    milestones: [
      {
        n: '01',
        window: 'Ngày 1–30',
        title: 'Pilot cùng nhà nhập khẩu',
        body: 'Chạy thử với 1–2 nhà nhập khẩu EU: xuất Lot Passport + GeoJSON cho TRACES NT, đối chiếu với DDS thực tế của họ.',
      },
      {
        n: '02',
        window: 'Ngày 31–60',
        title: 'Mở rộng vùng trồng',
        body: 'Nhân rộng sang các HTX và vùng trồng cà phê mới, hiệu chỉnh quy trình tiếp nhận thửa và đối soát Sentinel-2 · JRC TMF · GFC.',
      },
      {
        n: '03',
        window: 'Ngày 61–90',
        title: 'Tích hợp hardware đo đạc',
        body: 'Kết nối thiết bị GNSS/ranh giới ngoài thực địa để cải thiện độ chính xác toạ độ thửa và rút ngắn thời gian xác minh verdict.',
      },
    ] as Milestone[],
  },
  en: {
    kicker: 'TRACTION',
    title: <>Traction and the <em>next 90 days</em>.</>,
    sub: 'The numbers below are illustrative TARGETS for the pilot phase — not results already achieved. They exist to measure progress honestly.',
    targetTag: 'Target',
    stats: [
      { value: 5000, decimals: 0, suffix: '', label: 'plots mapped (pilot target)' },
      { value: 1200, decimals: 0, suffix: ' ha', label: 'area screened (target)' },
      { value: 8, decimals: 0, suffix: '', label: 'co-ops in pilot (target)' },
      { value: 90, decimals: 0, suffix: '%', label: 'verdicts auto-generated (target)' },
    ] as Stat[],
    roadmapTag: '// 07 · NEXT 90 DAYS',
    roadmapTitle: 'Next 90 days',
    roadmapNote: 'Targets, not committed results.',
    milestones: [
      {
        n: '01',
        window: 'Day 1–30',
        title: 'Pilot with buyers',
        body: 'Run with 1–2 EU importers: export Lot Passport + GeoJSON to TRACES NT and reconcile against their real DDS submissions.',
      },
      {
        n: '02',
        window: 'Day 31–60',
        title: 'Expand growing regions',
        body: 'Scale to new co-ops and coffee regions, tuning plot intake and the Sentinel-2 · JRC TMF · GFC cross-checks.',
      },
      {
        n: '03',
        window: 'Day 61–90',
        title: 'Integrate field hardware',
        body: 'Connect on-the-ground GNSS / boundary devices to sharpen plot coordinates and shorten verdict verification time.',
      },
    ] as Milestone[],
  },
} as const

export default function Traction() {
  const { lang } = useI18n()
  const C = DATA[lang]

  return (
    <section id="traction" className="section traction">
      <div className="container">
        <SectionHeader index="07" kicker={C.kicker} title={C.title} sub={C.sub} />

        <div className="traction-stats">
          {C.stats.map((s, i) => (
            <Reveal key={s.label} delay={0.06 * i} y={20}>
              <HudCard
                className="traction-stat"
                header={<><span className="label">{C.targetTag}</span></>}
                brackets
              >
                <div className="traction-stat-num num">
                  <MetricTicker
                    value={s.value}
                    decimals={s.decimals}
                    prefix={s.prefix ?? ''}
                    suffix={s.suffix}
                  />
                </div>
                <div className="traction-stat-label">{s.label}</div>
              </HudCard>
            </Reveal>
          ))}
        </div>

        <Reveal className="traction-roadmap" delay={0.1} y={24}>
          <HudCard
            className="traction-roadmap-card"
            header={<>{C.roadmapTag}</>}
            brackets
          >
            <div className="traction-roadmap-head">
              <h3 className="traction-roadmap-title">{C.roadmapTitle}</h3>
              <span className="traction-roadmap-note">{C.roadmapNote}</span>
            </div>
            <ol className="traction-roadmap-list">
              {C.milestones.map((m, i) => (
                <Reveal key={m.n} delay={0.16 + 0.08 * i} y={16}>
                  <li className="traction-milestone">
                    <span className="traction-milestone-n mono">{m.n}</span>
                    <div className="traction-milestone-body">
                      <span className="traction-milestone-window label">{m.window}</span>
                      <h4 className="traction-milestone-title">{m.title}</h4>
                      <p className="traction-milestone-text">{m.body}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </HudCard>
        </Reveal>
      </div>
    </section>
  )
}
